import { useEffect, useState } from 'react';
import { X, Package, Truck, AlertTriangle, Tag, ArrowRight } from 'lucide-react';
import { marginCorrectionsApi } from '../../services/marginCorrectionsApi';
import type { LineOriginResponse } from '../../types/marginCorrections';

const fmt = (n: number | null | undefined, d = 2) =>
    n == null ? '–' : n.toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-CH') : '–');

interface Props {
    company: string;
    documentID: string;
    onClose: () => void;
    onCorrect: () => void;
    canEdit: boolean;
}

/**
 * Left-click side panel: where the cost of one invoice line came from — lot → goods receipt →
 * vendor → purchase date, cost share per lot, and any corrections already booked.
 */
export function LineOriginPanel({ company, documentID, onClose, onCorrect, canEdit }: Props) {
    const [data, setData] = useState<LineOriginResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        marginCorrectionsApi.getLineOrigin(company, documentID)
            .then(res => { if (!cancelled) setData(res); })
            .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [company, documentID]);

    // Esc closes the panel.
    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);

    const line = data?.line;

    return (
        <div className="fixed inset-y-0 right-0 w-[440px] bg-white shadow-2xl border-l z-40 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Line Origin</h3>
                    <p className="text-xs text-gray-500 font-mono">{documentID}</p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                )}
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

                {!loading && line && (
                    <>
                        {/* Line summary */}
                        <div className="space-y-1.5">
                            <div className="flex justify-between text-sm"><span className="text-gray-500">Customer</span><span className="font-medium text-right">{line.customerName}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-500">Product</span><span className="text-right max-w-[260px] truncate" title={line.productName}>{line.productName}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-500">Posting date</span><span>{fmtDate(line.postingDate)}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-500">Quantity</span><span>{fmt(line.quantity, 0)} {line.salesUnit}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-500">Net sales</span><span>{fmt(line.netSalesEUR)} EUR</span></div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Cost {line.correctionDeltaEUR !== 0 && <span className="text-blue-600">(corrected)</span>}</span>
                                <span className="font-medium">
                                    {line.correctionDeltaEUR !== 0 && <span className="text-gray-400 line-through mr-1">{fmt(line.originalCostEUR)}</span>}
                                    {fmt(line.costEUR)} EUR
                                </span>
                            </div>
                        </div>

                        {/* Reconciliation warning */}
                        {!line.costReconciled && (
                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-xs">
                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>The lot chain does not fully reconcile to the booked cost for this line. The origin below is indicative — refresh the lot data or treat with care.</span>
                            </div>
                        )}
                        {line.linkStatus === 'NotItemBased' && (
                            <div className="bg-gray-50 border text-gray-600 px-3 py-2 rounded text-xs">
                                This line has no goods movement (e.g. a commission line), so there is no lot origin to show.
                            </div>
                        )}

                        {/* Origins */}
                        {data.origins.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                                    <Package className="w-3.5 h-3.5" /> Lots &amp; purchase origin
                                </h4>
                                <div className="space-y-2">
                                    {data.origins.map((o, i) => (
                                        <div key={`${o.lotCode}-${o.inboundEntryNo}-${i}`} className={`border rounded-lg p-3 ${o.isSpecialLot ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="font-mono text-sm font-medium flex items-center gap-1.5">
                                                    {o.isSpecialLot && <Tag className="w-3.5 h-3.5 text-purple-600" />}
                                                    {o.lotCode || '(no lot)'}
                                                </span>
                                                <span className="text-xs text-gray-500">{fmt(o.appliedQuantity, 0)} @ {fmt(o.purchaseUnitCostEUR)}</span>
                                            </div>
                                            <div className="space-y-1 text-xs text-gray-600">
                                                <div className="flex items-center gap-1.5">
                                                    <Truck className="w-3 h-3 text-gray-400" />
                                                    <span>{o.originKind}</span>
                                                    {o.receiptNo && <span className="font-mono">· {o.receiptNo}</span>}
                                                    {o.receiptDate && <span>· {fmtDate(o.receiptDate)}</span>}
                                                </div>
                                                {o.vendorName && (
                                                    <div className="flex items-center gap-1.5">
                                                        <ArrowRight className="w-3 h-3 text-gray-400" />
                                                        <span>{o.vendorName}{o.vendorNo && <span className="text-gray-400 font-mono"> ({o.vendorNo})</span>}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between pt-1 border-t mt-1">
                                                    <span className="text-gray-500">Cost share</span>
                                                    <span className="font-medium text-gray-700">{fmt(o.appliedCostEUR)} EUR</span>
                                                </div>
                                                {o.costDeltaEUR != null && Math.abs(o.costDeltaEUR) > 0.005 && (
                                                    <div className="flex justify-between text-amber-600">
                                                        <span>Δ booked vs. expected</span>
                                                        <span>{o.costDeltaEUR > 0 ? '+' : ''}{fmt(o.costDeltaEUR)} EUR</span>
                                                    </div>
                                                )}
                                            </div>
                                            {o.isSpecialLot && (
                                                <div className="mt-2 pt-2 border-t border-purple-200 text-xs text-purple-800">
                                                    Special-price lot{o.intendedCustomerName ? <> — bought for <span className="font-medium">{o.intendedCustomerName}</span></> : ''}
                                                    {o.negotiatedUnitCostEUR != null && <> · negotiated {fmt(o.negotiatedUnitCostEUR)}/unit</>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Existing corrections */}
                        {data.corrections.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Corrections on this line</h4>
                                <div className="space-y-2">
                                    {data.corrections.map(c => (
                                        <div key={c.groupId} className="border rounded-lg p-3 text-xs bg-blue-50 border-blue-200">
                                            <div className="flex justify-between font-medium text-blue-900">
                                                <span>Group #{c.groupId}</span>
                                                <span>{c.deltaEUR > 0 ? '+' : ''}{fmt(c.deltaEUR)} EUR GP</span>
                                            </div>
                                            <p className="text-gray-600 mt-1">{c.reason}</p>
                                            {c.counterLines.length > 0 && (
                                                <div className="mt-1.5 pt-1.5 border-t border-blue-200 text-gray-500">
                                                    Counter: {c.counterLines.map(cl => `${cl.documentCode} (${cl.deltaEUR > 0 ? '+' : ''}${fmt(cl.deltaEUR)})`).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {canEdit && line && line.linkStatus !== 'NotItemBased' && (
                <div className="border-t p-3 bg-gray-50">
                    <button onClick={onCorrect} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
                        Correct cost…
                    </button>
                </div>
            )}
        </div>
    );
}
